/**
 * boot-skeleton.js — the shape of the command bar, before there is one.
 *
 * ── What it replaces, and why ─────────────────────────────────────────────
 * The boot shell used to draw ONE shimmering block across `#pb-toolbar`, sized
 * by `--pb-toolbar-height` (56px). The bar it stands in for is two rows and
 * measures 90px, so the canvas sat 34px too high for the whole load and dropped
 * the moment the real bar mounted. On a phone it was wrong the other way — 56px
 * reserved for a 46px top strip — and it drew nothing at all for the 45px bottom
 * bar, which is where every action lives there.
 *
 * A skeleton exists to promise a layout. One that promises the wrong one is
 * worse than none: the jump it produces is the exact failure the two-row design
 * was built to avoid.
 *
 * ── Why it is built from the bar's own row classes ────────────────────────
 * `.pb-cb-row1` / `.pb-cb-row2` carry the heights, the borders and the phone
 * media query that moves row 2 to the bottom of the screen. Reusing them means
 * the skeleton cannot drift from the bar by a pixel, and the phone's bottom bar
 * is represented for free rather than by a second set of rules that someone has
 * to remember to update. The gate in `command-bar-geometry.mjs` measures both
 * and fails on any difference.
 *
 * ── Lifetime ──────────────────────────────────────────────────────────────
 * Mounted when the boot shell reveals (auth start) and removed the moment the
 * REAL bar is constructed — not at the entrance animation. Once the bar exists,
 * a skeleton of it is a lie, and on a phone the two would fight over the same
 * fixed bottom strip.
 */

const ROOT_CLASS = 'pb-boot-sk';

/**
 * One shimmer block. `w` is a CSS length; the height is the bar's own control
 * height, so the blocks line up with the controls they stand in for.
 */
/**
 * One shimmer block. `as` names the control it stands in for, so the phone rules
 * below can hide the same regions the real bar hides — by NAME. The first draft
 * used `:nth-child()` and hid the wrong four boxes the moment a spacer counted
 * as a child; a positional selector describing a layout is a bug waiting for the
 * next insertion.
 */
function block(width, as, { round = 'pill' } = {}) {
  const el = document.createElement('span');
  el.className = `pb-boot-sk-block pb-boot-sk-${round} pb-boot-sk-as-${as}`;
  if (width) el.style.inlineSize = width;
  return el;
}

function icon(as) {
  return block(null, as, { round: 'sq' });
}

function spacer({ zone = false } = {}) {
  const el = document.createElement('span');
  el.className = 'pb-boot-sk-spacer';
  // Row 2's height does NOT come from its min-height alone: `.pb-cb-slot-context`
  // is `height: var(--pb-cb-row2-h)`, and with border-box that content box plus
  // the row's two 1px borders is what makes the real row 44 rather than 42. A
  // skeleton without an equivalent child is two pixels short — which is exactly
  // the drift this file exists to prevent, so the spacer carries it.
  if (zone) el.classList.add('pb-boot-sk-zone');
  return el;
}

/**
 * The two rows, in the order the real bar builds them. The widths are the
 * measured resting widths of the controls they replace — a skeleton that
 * promises a 40px chip where a 150px project chip lands is a second jump.
 */
function buildRow1() {
  const row = document.createElement('div');
  row.className = 'pb-cb-row pb-cb-row1 pb-boot-sk-row';
  row.append(
    icon('sidebar'),
    block('150px', 'chip'), // project chip + its arrow
    block('62px', 'tab'),
    block('54px', 'tab'),
    spacer(),
    block('78px', 'save'),
    icon('agent'),
    block('84px', 'share'),
    block('104px', 'publish'),
    icon('account')
  );
  return row;
}

function buildRow2() {
  const row = document.createElement('div');
  row.className = 'pb-cb-row pb-cb-row2 pb-boot-sk-row';
  row.append(
    icon('undo'),
    icon('redo'),
    // Phone-only, and only ever visible there — the same two triggers the real
    // bottom bar mounts beside undo/redo.
    icon('sheet'),
    icon('sheet'),
    block('92px', 'verb'),
    block('76px', 'verb'),
    block('68px', 'verb'),
    spacer({ zone: true }),
    block('128px', 'viewport'),
    block('74px', 'zoom'),
    icon('preview'),
    icon('help')
  );
  return row;
}

/**
 * Mount the skeleton into the toolbar host. Idempotent.
 *
 * @param {HTMLElement} toolbarEl — `#pb-toolbar`
 * @returns {HTMLElement|null} the skeleton root
 */
export function mountBootSkeleton(toolbarEl) {
  if (!toolbarEl) return null;
  const existing = toolbarEl.querySelector(`.${ROOT_CLASS}`);
  if (existing) return existing;

  const root = document.createElement('div');
  // NOT `.pb-cb`, and this is the CMD-7 trap repeating: that class carries
  // `container-type: inline-size` and `backdrop-filter`, and EITHER establishes
  // a containing block for fixed-position descendants — so the phone's
  // `position: fixed` row 2 resolved against this element instead of the
  // viewport and sat under row 1, looking exactly like a media query that had
  // not matched. Measured at `row2Bottom: 46` against a viewport of 844, which
  // is the same number the phone geometry gate reported in CMD-7.
  root.className = ROOT_CLASS;
  // Decorative in full: the app already announces its loading state through
  // `aria-busy` on `.pb-app` and the boot placeholder's `role="status"`. Ten
  // more empty boxes in the accessibility tree would be noise on top of it.
  root.setAttribute('aria-hidden', 'true');
  root.append(buildRow1(), buildRow2());
  toolbarEl.appendChild(root);
  return root;
}

/** Remove it. Safe to call when it was never mounted, or twice. */
export function unmountBootSkeleton(toolbarEl = null) {
  const scope = toolbarEl || document;
  for (const el of scope.querySelectorAll(`.${ROOT_CLASS}`)) el.remove();
}
